import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Lib
import { planCards } from '@/lib/pricing/planCards';

// Components
import Icon from '@/components/base/Icon';

type WrapperPlanProps = {
    children: React.ReactNode;
};

function WrapperPlan({ children }: WrapperPlanProps) {
    return (
        <div className="relative overflow-hidden rounded-xl border border-icoGray-300 bg-icoGray-100 p-5 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite">
            <div className="relative z-1">{children}</div>
            <div className="absolute left-0 top-0 h-full w-full bg-icoRecapPlanSubscription" />
        </div>
    );
}

export function RecapCurrentPlan() {
    const { t } = useTranslation('commons');
    const { user } = useUser();
    const { subscription, isLoading } = useSubscription();

    if (isLoading) {
        return (
            <WrapperPlan>
                <div className="flex items-center text-9 uppercase leading-none tracking-0.5 text-icoBlue">
                    {t('pricing.your_current_plan')}
                </div>
                <div className="mt-2.5 animate-pulse space-y-1 text-sm">
                    <div className="h-4 w-15 rounded bg-icoGray-400/30 dark:bg-icoDarkMode-deepBlack" />
                    <div className="h-4 w-40 rounded bg-icoGray-400/30 dark:bg-icoDarkMode-deepBlack" />
                    <div className="h-4 w-40 rounded bg-icoGray-400/30 dark:bg-icoDarkMode-deepBlack" />
                </div>
            </WrapperPlan>
        );
    }

    if (!subscription) {
        return (
            <WrapperPlan>
                <div className="flex items-center text-9 uppercase leading-none tracking-0.5 text-icoBlue">
                    {t('pricing.your_current_plan')}
                </div>
            </WrapperPlan>
        );
    }

    const nbProfiles = subscription.options.profiles?.count_total ?? '∞';
    const nbUsers = subscription.options.users?.count_total ?? '∞';
    const nbGroups = user?.quotas?.groups?.purchased ?? '∞';
    const icon = planCards?.[subscription.product_key]?.icon;

    return (
        <WrapperPlan>
            <div className="flex items-center text-9 uppercase leading-none tracking-0.5 text-icoBlue">
                {t('pricing.your_current_plan')}
            </div>
            <div className="mt-2.5 space-y-1 text-sm">
                <p className="flex items-center gap-1 text-base">
                    {t(
                        `commons:pricing.plans.${subscription.product_key}`,
                        {},
                        { fallback: `commons:subscriptions.${subscription.product_title}` }
                    )}
                    {icon ? <Icon icon={icon} className="text-icoBlue" /> : null}
                </p>
                <p className="font-GMedium">
                    <span className="font-GBold leading-none">{nbProfiles}</span>{' '}
                    {t('commons:subscriptions.nb_profiles')}
                </p>
                <p className="font-GMedium">
                    <span className="font-GBold leading-none">{nbUsers}</span>{' '}
                    {t('commons:subscriptions.nb_users')}
                </p>
                {nbGroups !== 0 ? (
                    <p>
                        <span className="font-GBold leading-none">{nbGroups}</span>{' '}
                        {t('commons:subscriptions.nb_groups')}
                    </p>
                ) : null}
            </div>
        </WrapperPlan>
    );
}
